/* eslint-disable react-refresh/only-export-components -- come ActiveMatchContext: il provider e il
   suo hook stanno insieme, e separarli vorrebbe dire due file per un solo contratto. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { env } from '@/env';
import {
  gameIdsOfProfileTypes,
  queueListForGames,
  queueRefusalOf,
  ticketToTell,
  type LimuloQueueRefusal,
  type LimuloQueueTicket,
  type QueueJoinInput,
} from '@/lib/limulo/code';
import { limuloFailure, type LimuloFailure } from '@/lib/limulo/errors';
import { giochiConCodeDaRiprendere } from '@/lib/limulo/sitngoAcceso';
import { startRenewLoop, type RenewLoop } from '@/lib/limulo/rinnovoCoda';
import {
  limuloApi,
  useGetLimuloAzioniQuery,
  useGetLimuloGamesQuery,
  useGetLimuloQueuesQuery,
  useGetMyLimuloTournamentsConTotaleQuery,
  usePostLimuloQueueJoinMutation,
  usePostLimuloQueueLeaveMutation,
  usePostLimuloQueueRenewMutation,
} from '@/services/limuloApi';
import { haSessione } from '@/lib/ospite';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

/**
 * IL BIGLIETTO DI CODA, tenuto dall'APP e non dalla schermata (qTournaments 1.72.0, kb#282).
 *
 * 🔴 **Perche' qui e non nel pannello «Ranked».** Il rinnovo dice al motore che il giocatore c'e':
 * se vivesse nel pannello, cambiare scheda della Hub (o aprire un torneo) lo smonterebbe, e dopo
 * ~45 s il giocatore uscirebbe dalla coda **senza averlo chiesto** — mentre l'app e' aperta davanti
 * a lui. Il contratto chiede il rinnovo «solo mentre l'app e' aperta in primo piano»: e' l'APP a
 * dover essere in primo piano, non una scheda. ⇒ Il provider sta in `AppShell`, e il rinnovo si
 * ferma solo con l'app nascosta (`rinnovoCoda.ts`), con «Esci», o a biglietto chiuso.
 *
 * ⚠️ Da solo chiede al server una cosa sola: la lista, UNA volta all'avvio, per riprendere un'attesa
 * dopo un ricaricamento (v. `ripreso`). Il resto parte con `join` o con `adopt` (anche la «Ranked»
 * adotta dalla sua lista), e a flag spento non monta nemmeno (`CodeTorneiProvider` rende solo i figli).
 */

export interface CodeTorneiValue {
  /**
   * L'ultimo biglietto noto (da join, leave, rinnovo o lista). `null` = nessuno.
   * 🔴 I biglietti sono UNO PER GIOCO (come sul server): questo e' quello toccato per ultimo, o, se quello
   * si e' chiuso, uno in attesa. Chi racconta le code di UN gioco (i pannelli della Hub) non lo legge: legge
   * `tickets` con `ticketOfGames` (`useCodeTorneiPannello`), perche' il biglietto di un altro gioco non e'
   * la sua attesa. Con un gioco solo (oggi: Clash Royale) i due coincidono.
   */
  ticket: LimuloQueueTicket | null;
  /** Tutti i biglietti noti, per `gameId` (uno per gioco). Vuoto = nessuno. */
  tickets: Readonly<Record<string, LimuloQueueTicket>>;
  /** Il rifiuto NOMINATO dell'ultima scrittura (`details.queueReason`), o `null`. */
  refusal: LimuloQueueRefusal | null;
  /** Il `gameId` su cui ha scritto l'ultima scrittura (join o leave): a chi appartengono `refusal` e `failure`. */
  scrittoSu: string | null;
  /** L'ultima scrittura fallita senza un motivo nominato: la racconta `LimuloDegraded`. */
  failure: LimuloFailure | null;
  /** Una scrittura (join/leave) e' in volo. */
  busy: boolean;
  join: (input: QueueJoinInput & { gameId: string }) => Promise<void>;
  /**
   * Esce da tutte le code del gioco; con `queueId`, solo da quel modello (il pannello «Sit&Go»,
   * riga per riga). Se era l'ultimo, il biglietto si chiude come per un'uscita intera.
   * Il gioco e' `gameId` se dato (i pannelli passano il loro), altrimenti quello di `ticket`.
   */
  leave: (queueId?: string, gameId?: string) => Promise<void>;
  /** Riprende un'attesa: adotta il biglietto letto dalla lista SOLO se l'app non ne ha ancora uno. */
  adopt: (ticket: LimuloQueueTicket | null) => void;
  /** Dimentica il rifiuto mostrato (l'utente ha letto e chiuso). */
  clearRefusal: () => void;
  /**
   * Chiede di rinnovare il biglietto ADESSO, senza aspettare il giro dei 20 s. Per chi ha la prova che il
   * motore l'ha appena cambiato (un «Accetta» di sfida che ha risposto: `useRispostaSfida`): fino al giro
   * dopo, le righe e la barra raccontano un'attesa che non c'e' piu'. ⚠️ Non e' un secondo stato: la
   * risposta arriva per la via di sempre e cambia `ticket`.
   * Rinnova SOLO se il CICLO di rinnovo vivo e' di quel `gameId` (il gioco del ciclo armato dall'ultimo
   * `join`, `adopt` o `leave`, non dell'ultimo biglietto reso). Non fa niente — e non lancia — senza
   * ciclo, con quello di un altro gioco, a biglietto chiuso, ad app nascosta, fuori dal provider o a flag
   * spento. Con un rinnovo del ciclo gia' in volo non ne lancia un secondo: ne segna uno per dopo la
   * risposta. ⚠️ Vale per i rinnovi del CICLO: la rilettura di `leave` rifiutato con `queue.not-waiting`
   * e' un rinnovo diretto, fuori dal ciclo, e con quella in volo due rinnovi possono stare in volo insieme.
   * ⚠️ Non tocca `busy`, `refusal` ne' `failure`, e non riarma il ciclo.
   * ⛔ Si chiama da un gestore o da una promessa (la risposta dell'accept), MAI da un effetto che dipende da
   * `ticket`: ogni risposta di rinnovo e' un biglietto nuovo, e l'effetto girerebbe a vuoto alla velocita'
   * della rete.
   */
  rinnovaOra: (gameId: string) => void;
}

const NOOP: CodeTorneiValue = {
  ticket: null,
  tickets: {},
  refusal: null,
  scrittoSu: null,
  failure: null,
  busy: false,
  join: async () => {},
  leave: async () => {},
  adopt: () => {},
  clearRefusal: () => {},
  rinnovaOra: () => {},
};

const Ctx = createContext<CodeTorneiValue>(NOOP);

export const useCodeTornei = (): CodeTorneiValue => useContext(Ctx);

const inAttesa = (t: LimuloQueueTicket | null): t is LimuloQueueTicket =>
  t !== null && (t.state === 'waiting' || t.state === 'matched');

function CodeTorneiAttivo({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const [joinQ] = usePostLimuloQueueJoinMutation();
  const [leaveQ] = usePostLimuloQueueLeaveMutation();
  const [renewQ] = usePostLimuloQueueRenewMutation();
  // 🔴 UN biglietto PER GIOCO (come sul server, `biglietto.model.ts`), e il gioco toccato per ultimo. Un solo
  // stato, cosi' i due cambiano insieme.
  const [biglietti, setBiglietti] = useState<{
    tickets: Record<string, LimuloQueueTicket>;
    ultimo: string | null;
  }>({ tickets: {}, ultimo: null });
  const { tickets } = biglietti;
  // Quello che `ticket` racconta (`ticketToTell`): l'ultimo toccato se e' vivo; se si e' chiuso, un'attesa viva
  // di un altro gioco; altrimenti l'ultimo, chiuso.
  const ticket = useMemo<LimuloQueueTicket | null>(
    () => ticketToTell(biglietti.tickets, biglietti.ultimo),
    [biglietti],
  );
  const [refusal, setRefusal] = useState<LimuloQueueRefusal | null>(null);
  const [failure, setFailure] = useState<LimuloFailure | null>(null);
  const [scrittoSu, setScrittoSu] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Un ciclo di rinnovo per gioco: scritto in `accept` (in modo sincrono) e tolto da `stopLoop`. E' anche
  // «il gioco del ciclo vivo» di `rinnovaOra`. Non e' `attuale`: quello si aggiorna in un effetto, dopo l'`accept`.
  const cicli = useRef(new Map<string, RenewLoop>());
  const attuale = useRef<Record<string, LimuloQueueTicket>>({});
  useEffect(() => {
    attuale.current = tickets;
  }, [tickets]);
  /**
   * Consegna il biglietto di UN gioco (o lo toglie, con `null`). `tocca`: lo segna come l'ultimo toccato — lo
   * fa chi ha scritto (join, leave, ripresa), MAI il rinnovo: un rinnovo in background non deve spostare
   * `ticket` da un gioco all'altro.
   */
  const metti = useCallback((gameId: string, next: LimuloQueueTicket | null, tocca: boolean) => {
    setBiglietti((prev) => {
      const tickets = { ...prev.tickets };
      if (next === null) delete tickets[gameId];
      else tickets[gameId] = next;
      let { ultimo } = prev;
      if (next === null) {
        if (ultimo === gameId) ultimo = null;
      } else if (tocca) {
        ultimo = gameId;
      }
      return { tickets, ultimo };
    });
  }, []);

  /**
   * 🔴 «Partita trovata»: il torneo e' appena nato, e `/mine` — da cui l'azione persistente trova
   * l'incontro e il suo check-in — lo vedrebbe solo al giro dopo (fino a 30 s). Si rilegge SUBITO,
   * una volta, al passaggio da un'attesa a `idle` abbinato. Da un'attesa, non solo da `matched`: il
   * motore crea e avvia il torneo nello stesso giro in cui abbina, e chi rinnova ogni 20 s passa
   * quasi sempre da `waiting` a `idle` senza vedere `matched`. ⚠️ Un biglietto gia' chiuso adottato
   * dalla lista (dopo un ricaricamento) non e' un passaggio: li' `/mine` si legge comunque.
   */
  const prima = useRef<Record<string, LimuloQueueTicket>>({});
  useEffect(() => {
    const ere = prima.current;
    prima.current = tickets;
    const passato = Object.values(tickets).some(
      (t) => inAttesa(ere[t.gameId] ?? null) && t.state === 'idle' && t.exitReason === 'abbinato',
    );
    if (passato) dispatch(limuloApi.util.invalidateTags(['LimuloTournament']));
  }, [dispatch, tickets]);

  /**
   * 🔴 L'ABBINAMENTO VISTO DALLA LETTURA DELLA BARRA. Il biglietto si rinnova ogni `renewEveryMs` (20 s), e la via
   * veloce del motore — `Tournament.queue.matched` sul realtime per utente — la TMA non la apre. Chi
   * entra e viene abbinato al giro dopo (4 s) restava «in coda» nella «Ranked» fino a 20 s, mentre la
   * barra mostrava gia' il check-in del torneo nuovo letto da `/mine` [reperto di Luca, 26/09 ~11:14Z:
   * «rimane lo spin su you are in the queue», con la finestra di check-in di un minuto].
   * ⇒ Mentre si aspetta, un torneo NUOVO nella lettura della barra e' la prova che il biglietto puo'
   * essere cambiato: si rinnova SUBITO (`ora`), e la risposta — il biglietto vero — porta il pannello
   * a «partita trovata». La lettura e' QUELLA CHE LA BARRA FA IN QUESTA BUILD, con lo stesso
   * interruttore di `AzionePersistente` (revisione di #433): ad azioni del motore accese (stg-tma)
   * `/v1/me/tournaments/actions`, che il campanello del tempo reale fa rileggere; altrimenti `/mine`.
   * Stessa chiave di cache, nessun ritmo suo: si ascolta e basta. La prima lettura dopo l'ingresso e'
   * il confronto, non una prova.
   * ⚠️ Residui dichiarati: a cache fredda (nessuno l'aveva letta prima, es. chi non ha la barra) la
   * prima lettura puo' arrivare DOPO l'abbinamento, e allora il torneo nuovo e' gia' nel confronto:
   * si torna al rinnovo dei 20 s. E chi e' in coda senza barra fa DUE letture in piu' per giro di
   * coda — all'ingresso e dopo il passaggio a `idle` abbinato — piu' una a ogni ritorno in primo
   * piano (`refetchOnFocus`). Quella dopo `idle` c'e' anche ad azioni ACCESE: l'invalidazione
   * dell'abbinamento (sopra) gira prima che `inCoda` falso spenga la sottoscrizione (misurato nella
   * revisione di #433, issuecomment-5846169108 punto 2: `/actions` a +20 s senza barra).
   */
  const inCoda = Object.values(tickets).some(inAttesa);
  const azioniAccese = env.featureTournamentActionsEnabled;
  const mine = useGetMyLimuloTournamentsConTotaleQuery(undefined, { skip: !inCoda || azioniAccese });
  const azioni = useGetLimuloAzioniQuery(undefined, { skip: !inCoda || !azioniAccese });
  const tornei = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!inCoda) {
      tornei.current = null;
      return;
    }
    const letti = azioniAccese
      ? azioni.data?.items.map((a) => a.tournamentId)
      : mine.data?.items.map((t) => t.tournamentId || t.id);
    if (!letti) return;
    const ids = new Set(letti);
    const visti = tornei.current;
    tornei.current = ids;
    // Un torneo nuovo puo' aver cambiato il biglietto di QUALUNQUE gioco in attesa: li si rinnova tutti.
    if (visti !== null && [...ids].some((id) => !visti.has(id))) {
      for (const ciclo of cicli.current.values()) ciclo.ora();
    }
  }, [inCoda, azioniAccese, mine.data, azioni.data]);

  /** Ferma il rinnovo di UN gioco: gli altri giochi in coda continuano a rinnovare. */
  const stopLoop = useCallback((gameId: string) => {
    cicli.current.get(gameId)?.stop();
    cicli.current.delete(gameId);
  }, []);

  /**
   * Consegna un biglietto nuovo e, se si aspetta, (ri)arma il rinnovo da li'. `subito` per un
   * biglietto RIPRESO dalla lista: non si sa quando e' stato rinnovato, il primo giro non aspetta.
   */
  const accept = useCallback(
    (next: LimuloQueueTicket | null, subito = false, gioco?: string) => {
      // Il gioco: quello del biglietto, o — se il server non ne ha reso uno — quello su cui si e' scritto.
      const gameId = next?.gameId ?? gioco;
      if (gameId === undefined) return;
      metti(gameId, next, true);
      stopLoop(gameId);
      if (!inAttesa(next)) return;
      cicli.current.set(gameId, startRenewLoop(
        {
          renew: async () => {
            const res = await renewQ({ gameId });
            return 'data' in res
              ? { ticket: res.data ?? null, error: null }
              : { ticket: null, error: res.error };
          },
          isVisible: () => document.visibilityState === 'visible',
          onVisibilityChange: (listener) => {
            document.addEventListener('visibilitychange', listener);
            return () => document.removeEventListener('visibilitychange', listener);
          },
          setTimer: (fn, ms) => window.setTimeout(fn, ms),
          clearTimer: (h) => window.clearTimeout(h),
          // Il rinnovo consegna e basta: il ciclo lo governa `startRenewLoop`, che si ferma da solo
          // a biglietto chiuso. Riarmarlo qui ne creerebbe un secondo a ogni giro.
          onTicket: (letto) => metti(gameId, letto, false),
        },
        next,
        { subito },
      ));
    },
    [metti, renewQ, stopLoop],
  );

  useEffect(() => {
    // La mappa e' una sola per tutta la vita del provider: la si legge qui, non nella pulizia.
    const attivi = cicli.current;
    return () => {
      for (const ciclo of attivi.values()) ciclo.stop();
      attivi.clear();
    };
  }, []);

  const join = useCallback<CodeTorneiValue['join']>(
    async (input) => {
      setBusy(true);
      setRefusal(null);
      setFailure(null);
      setScrittoSu(input.gameId);
      const res = await joinQ(input);
      setBusy(false);
      if ('data' in res) {
        accept(res.data ?? null, false, input.gameId);
        return;
      }
      const nominato = queueRefusalOf(res.error);
      if (nominato !== null) setRefusal(nominato);
      else setFailure(limuloFailure(res.error));
    },
    [accept, joinQ],
  );

  const leave = useCallback<CodeTorneiValue['leave']>(async (queueId, gioco) => {
    // Il biglietto di cui si esce: del gioco chiesto (i pannelli passano il loro), altrimenti l'ultimo toccato.
    const bersaglio = typeof gioco === 'string' && gioco !== '' ? tickets[gioco] : ticket;
    if (bersaglio === undefined || bersaglio === null) return;
    const gameId = bersaglio.gameId;
    // Solo una stringa vera nomina un modello: chi passasse `leave` come gestore di un click gli
    // darebbe l'evento, e da li' non deve nascere un'uscita parziale.
    const soloQuesta = typeof queueId === 'string' && queueId.trim() !== '' ? queueId : undefined;
    setBusy(true);
    setRefusal(null);
    setFailure(null);
    setScrittoSu(gameId);
    const res = await leaveQ(soloQuesta === undefined ? { gameId } : { gameId, queueId: soloQuesta });
    setBusy(false);
    if ('data' in res) {
      // ⚠️ Un'uscita PARZIALE lascia il biglietto in attesa, e il motore non ne tocca `lastSeenAt`
      // (`esciDallaCoda` scrive solo `entries`): riarmare il rinnovo da capo spingerebbe il prossimo
      // fino a 2 × `renewEveryMs` dall'ultimo, a ridosso dei ~45 s di `expiresAfterMs`. Si rinnova
      // subito, come per un biglietto ripreso.
      accept(res.data ?? null, soloQuesta !== undefined, gameId);
      return;
    }
    const nominato = queueRefusalOf(res.error);
    // ⚠️ «Non eri in coda» su un'uscita e' un'uscita riuscita: si rilegge dal rinnovo, che risponde
    // SEMPRE col biglietto vero, invece di inventarne uno chiuso.
    if (nominato?.reason === 'queue.not-waiting') {
      const letto = await renewQ({ gameId });
      accept('data' in letto ? (letto.data ?? null) : null, false, gameId);
      return;
    }
    if (nominato !== null) setRefusal(nominato);
    else setFailure(limuloFailure(res.error));
  }, [accept, leaveQ, renewQ, ticket, tickets]);

  /**
   * 🔴 SOLO per riprendere. Da quando l'app ha un biglietto, lo tengono aggiornato le sue scritture e
   * il rinnovo, che rispondono col biglietto vero; la lista si rilegge ogni 30 s e puo' essere piu'
   * vecchia di loro — adottarla dopo un «Esci» rimetterebbe in coda chi e' appena uscito.
   * ⚠️ Stabile (legge il biglietto da un ref): chi la usa in un effetto non si riesegue a ogni rinnovo.
   */
  const adopt = useCallback<CodeTorneiValue['adopt']>(
    (letto) => {
      // Un biglietto per gioco: si adotta solo se di QUEL gioco l'app non ne ha ancora uno.
      if (letto === null || attuale.current[letto.gameId] !== undefined) return;
      attuale.current = { ...attuale.current, [letto.gameId]: letto };
      accept(letto, true);
    },
    [accept],
  );

  /**
   * 🔴 Riprendere un'attesa ALL'AVVIO, dovunque si apra l'app. Se lo facesse solo la «Ranked», chi
   * ricarica mentre e' in coda e resta altrove non rinnoverebbe, e dopo ~45 s uscirebbe dalla coda
   * senza averlo chiesto. Si legge la lista UNA volta (poi la sottoscrizione si spegne) e si adotta
   * solo i biglietti IN ATTESA dei giochi che hanno un pannello delle code acceso
   * (`giochiConCodeDaRiprendere`: oggi Clash Royale): un biglietto chiuso non ha niente da rinnovare, e
   * il pannello lo racconta quando si apre, come prima.
   */
  // IL predicato della sessione (bridge || SDK, fail-closed): anche nel ripiego web si riprende.
  const authed = useAppSelector(haSessione);
  const [ripreso, setRipreso] = useState(false);
  const soloAllAvvio = { skip: !authed || ripreso };
  const listaAvvio = useGetLimuloQueuesQuery(undefined, soloAllAvvio);
  const giochiAvvio = useGetLimuloGamesQuery(undefined, soloAllAvvio);
  useEffect(() => {
    if (ripreso || listaAvvio.data === undefined || giochiAvvio.data === undefined) return;
    setRipreso(true);
    const ids = gameIdsOfProfileTypes(giochiAvvio.data, giochiConCodeDaRiprendere());
    const inCoda = ids ? queueListForGames(listaAvvio.data, ids).tickets.filter(inAttesa) : [];
    for (const biglietto of inCoda) adopt(biglietto);
  }, [ripreso, listaAvvio.data, giochiAvvio.data, adopt]);

  const clearRefusal = useCallback(() => {
    setRefusal(null);
    setFailure(null);
  }, []);

  /**
   * Una cosa sola: se il ciclo VIVO e' di quel gioco, gli chiede di rinnovare ora (`ora()`, lo stesso
   * gesto del torneo nuovo in `/mine` e del ritorno in primo piano). ⛔ Non e' `accept`: riarmare il ciclo
   * lo fermerebbe a meta' di un rinnovo in volo e ne lancerebbe un altro in parallelo, e non serve — il
   * ciclo c'e' gia'. Il ciclo di QUEL gioco sta in `cicli`, scritto in `accept` in modo sincrono: non
   * `attuale`, che si aggiorna in un effetto e per un istante dopo un `join` e' ancora il biglietto di
   * prima (chiamata persa, o rinnovo del ciclo di un altro gioco). A biglietto chiuso il ciclo e' fermo
   * (tolto da `stopLoop`) e `ora()` non parte: per questo qui non si guarda `inAttesa`, che sarebbe un
   * secondo cancello sulla stessa porta.
   * ⚠️ Stabile (legge i cicli da un ref): chi la mette fra le dipendenze di un effetto non riparte a
   * ogni biglietto nuovo.
   */
  const rinnovaOra = useCallback<CodeTorneiValue['rinnovaOra']>((gameId) => {
    cicli.current.get(gameId)?.ora();
  }, []);

  const value = useMemo<CodeTorneiValue>(
    () => ({
      ticket,
      tickets,
      refusal,
      failure,
      scrittoSu,
      busy,
      join,
      leave,
      adopt,
      clearRefusal,
      rinnovaOra,
    }),
    [ticket, tickets, refusal, failure, scrittoSu, busy, join, leave, adopt, clearRefusal, rinnovaOra],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * A flag spento rende solo i figli: nessun hook di RTK, nessun listener, nessun timer.
 * ⚠️ In AND con `featureLimuloEnabled`: le code passano dal BFF Limulo, e una build senza Limulo
 * che accendesse le code mostrerebbe una scheda che non puo' funzionare.
 */
export function CodeTorneiProvider({ children }: { children: ReactNode }) {
  if (!(env.featureTournamentQueuesEnabled && env.featureLimuloEnabled)) return <>{children}</>;
  return <CodeTorneiAttivo>{children}</CodeTorneiAttivo>;
}
